import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { initiateBulkPartnerAccountTransfer } from 'api/internaltools';
import {
  BULK_TRANSFER_CHUNK_SIZE,
  BULK_TRANSFER_CSV_COLUMNS,
  BULK_TRANSFER_RESULTS_FILE_NAME,
  BULK_TRANSFER_ROW_STATUS_FAILED,
  BULK_TRANSFER_SAMPLE_FILE_NAME,
  CSV_COLUMN_CREATE_CREDIT_RECORD,
  MAX_BULK_TRANSFER_ROWS,
  SUPPORTED_PARTNERS,
} from 'modules/partner-account-transfer/constants/constants';
import {
  buildFailedResult,
  buildNotProcessedResult,
  buildResultsCsvContent,
  buildResultsForChunk,
  buildSampleCsvContent,
  BulkTransferResultRow,
  chunkRows,
  downloadCsvFile,
  getInvalidRows,
  isSucceededStatus,
  parseBulkTransferCsv,
  ParsedBulkTransferFile,
  ParsedBulkTransferRow,
  readFileAsText,
} from 'modules/partner-account-transfer/utils/bulkTransfer';
import { showToast } from 'utils/notifications';

type BulkTransferModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const REQUIRED_COLUMN_KEYS = BULK_TRANSFER_CSV_COLUMNS.filter((column) => column.isRequired).map(
  (column) => column.key
);

const OPTIONAL_COLUMN_KEYS = BULK_TRANSFER_CSV_COLUMNS.filter((column) => !column.isRequired).map(
  (column) => column.key
);

const UNKNOWN_OUTCOME_PREFIX = 'Outcome unknown, verify on the partner before retrying';

const getApiErrorMessage = (error: any): string => {
  return error?.response?.data?.error?.message ?? error?.message ?? 'The request failed.';
};

const wasBatchRejectedBeforeExecuting = (error: any): boolean => {
  const status = error?.response?.status;

  return typeof status === 'number' && status >= 400 && status < 500;
};

const getStatusColorScheme = (status: string): string => {
  if (isSucceededStatus(status)) {
    return 'green';
  }

  return status === BULK_TRANSFER_ROW_STATUS_FAILED ? 'red' : 'gray';
};

export default function BulkTransferModal({ isOpen, onClose }: BulkTransferModalProps) {
  const toast = useToast();

  const [selectedPartnerCode, setSelectedPartnerCode] = useState('');
  const [fileName, setFileName] = useState('');
  const [parsedFile, setParsedFile] = useState<ParsedBulkTransferFile | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [results, setResults] = useState<BulkTransferResultRow[] | null>(null);

  const rows = parsedFile?.rows ?? [];
  const fileError = parsedFile?.fileError ?? '';
  const invalidRows = useMemo(() => getInvalidRows(rows), [rows]);
  const isSubmitDisabled = selectedPartnerCode === '' || rows.length === 0 || invalidRows.length > 0;
  const succeededCount = results?.filter((result) => isSucceededStatus(result.status)).length ?? 0;
  const hasUnknownOutcomeRows = (results ?? []).some((result) => result.error.startsWith(UNKNOWN_OUTCOME_PREFIX));

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    event.target.value = '';

    if (!file) {
      return;
    }

    setResults(null);
    setProcessedCount(0);
    setFileName(file.name);

    try {
      const content = await readFileAsText(file);

      setParsedFile(parseBulkTransferCsv(content));
    } catch (error) {
      setParsedFile({ rows: [], fileError: getApiErrorMessage(error) });
    }
  };

  const handleSubmit = async () => {
    const chunks = chunkRows<ParsedBulkTransferRow>(rows, BULK_TRANSFER_CHUNK_SIZE);
    const collectedResults: BulkTransferResultRow[] = [];

    let requestError = '';
    let hasUnknownOutcome = false;

    setIsSubmitting(true);
    setProcessedCount(0);
    setResults(null);

    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex += 1) {
      const chunk = chunks[chunkIndex];

      try {
        const payload = { transfers: chunk.map((row) => row.transfer) };
        const response = await initiateBulkPartnerAccountTransfer(selectedPartnerCode, payload);

        collectedResults.push(...buildResultsForChunk(chunk, response?.data?.data?.results ?? []));
        setProcessedCount(collectedResults.length);
      } catch (error) {
        requestError = getApiErrorMessage(error);

        if (wasBatchRejectedBeforeExecuting(error)) {
          chunk.forEach((row) => collectedResults.push(buildNotProcessedResult(row, requestError)));
        } else {
          hasUnknownOutcome = true;
          chunk.forEach((row) =>
            collectedResults.push(buildFailedResult(row, `${UNKNOWN_OUTCOME_PREFIX}: ${requestError}`))
          );
        }

        rows
          .slice(collectedResults.length)
          .forEach((row) =>
            collectedResults.push(buildNotProcessedResult(row, 'Skipped after an earlier batch failed.'))
          );

        break;
      }
    }

    setResults(collectedResults);
    setProcessedCount(collectedResults.length);
    setIsSubmitting(false);

    const succeeded = collectedResults.filter((result) => isSucceededStatus(result.status)).length;

    if (requestError !== '') {
      const description = hasUnknownOutcome ? `${UNKNOWN_OUTCOME_PREFIX}. ${requestError}` : requestError;

      showToast(toast, 'Bulk transfer stopped', description, 'error');
    } else if (succeeded < collectedResults.length) {
      showToast(
        toast,
        'Bulk transfer completed with failures',
        `${succeeded} of ${collectedResults.length} transfers succeeded.`,
        'warning'
      );
    } else {
      showToast(toast, 'Success', `All ${succeeded} transfers were initiated.`, 'success');
    }
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }

    onClose();
  };

  const renderFormatHelp = () => (
    <Box bg='gray.50' borderRadius='md' p={3}>
      <Text fontSize='sm' fontWeight='semibold' mb={1}>
        Expected CSV format
      </Text>
      <Code fontSize='xs' display='block' whiteSpace='pre-wrap' mb={2} bg='transparent' p={0}>
        {BULK_TRANSFER_CSV_COLUMNS.map((column) => column.key).join(',')}
      </Code>
      <Text fontSize='xs' color='gray.600'>
        Required: {REQUIRED_COLUMN_KEYS.join(', ')}. Optional: {OPTIONAL_COLUMN_KEYS.join(', ')}. Columns may appear in
        any order. Up to {MAX_BULK_TRANSFER_ROWS} transfers per file.
      </Text>
      <Text fontSize='xs' color='gray.600' mt={1}>
        {CSV_COLUMN_CREATE_CREDIT_RECORD} accepts true/false (blank means false) and books a credit record against the
        Varni Tech merchant for that row.
      </Text>
    </Box>
  );

  const renderPreview = () => (
    <Box>
      <HStack mb={2} spacing={3}>
        <Text fontSize='sm' fontWeight='semibold'>
          {rows.length} transfer(s) found in {fileName}
        </Text>
        {invalidRows.length > 0 && <Badge colorScheme='red'>{invalidRows.length} invalid</Badge>}
      </HStack>

      <TableContainer borderRadius='md' border='1px solid' borderColor='gray.200' maxH='320px' overflowY='auto'>
        <Table variant='simple' size='sm'>
          <Thead position='sticky' top={0} bg='gray.50' zIndex={1}>
            <Tr>
              <Th>Row</Th>
              <Th>Source account</Th>
              <Th>Beneficiary</Th>
              <Th isNumeric>Amount</Th>
              <Th>Purpose</Th>
              <Th>Credit record</Th>
              <Th>Issues</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row) => (
              <Tr key={row.rowNumber} bg={row.errors.length > 0 ? 'red.50' : undefined}>
                <Td>{row.rowNumber}</Td>
                <Td>{row.transfer.source_account_number || '-'}</Td>
                <Td>
                  <Text>{row.transfer.beneficiary_account_number || '-'}</Text>
                  {row.transfer.beneficiary_name && (
                    <Text fontSize='xs' color='gray.600'>
                      {row.transfer.beneficiary_name}
                    </Text>
                  )}
                </Td>
                <Td isNumeric>
                  {Number.isFinite(row.transfer.amount) ? row.transfer.amount : '-'} {row.transfer.currency}
                </Td>
                <Td>{row.transfer.purpose || '-'}</Td>
                <Td>{row.transfer.create_credit_record ? <Badge colorScheme='purple'>Yes</Badge> : <Text>No</Text>}</Td>
                <Td>
                  {row.errors.length === 0 ? (
                    <Badge colorScheme='green'>Valid</Badge>
                  ) : (
                    <Text fontSize='xs' color='red.600'>
                      {row.errors.join('; ')}
                    </Text>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderResults = () => (
    <Box>
      {hasUnknownOutcomeRows && (
        <Alert status='error' borderRadius='md' mb={3}>
          <AlertIcon />
          One or more batches did not return a result, so their transfers may or may not have been sent. Check those
          rows on the partner before re-uploading them, otherwise you risk sending them twice.
        </Alert>
      )}

      <HStack mb={2} spacing={3}>
        <Text fontSize='sm' fontWeight='semibold'>
          {succeededCount} of {results?.length ?? 0} transfers succeeded
        </Text>
        <Button
          size='xs'
          variant='link'
          colorScheme='blue'
          onClick={() => downloadCsvFile(BULK_TRANSFER_RESULTS_FILE_NAME, buildResultsCsvContent(results ?? []))}
        >
          Download results CSV
        </Button>
      </HStack>

      <TableContainer borderRadius='md' border='1px solid' borderColor='gray.200' maxH='320px' overflowY='auto'>
        <Table variant='simple' size='sm'>
          <Thead position='sticky' top={0} bg='gray.50' zIndex={1}>
            <Tr>
              <Th>Row</Th>
              <Th>Source account</Th>
              <Th>Beneficiary</Th>
              <Th isNumeric>Amount</Th>
              <Th>Status</Th>
              <Th>Reference</Th>
              <Th>Error</Th>
            </Tr>
          </Thead>
          <Tbody>
            {(results ?? []).map((result) => (
              <Tr key={result.rowNumber}>
                <Td>{result.rowNumber}</Td>
                <Td>{result.sourceAccountNumber}</Td>
                <Td>{result.beneficiaryAccountNumber}</Td>
                <Td isNumeric>
                  {result.amount} {result.currency}
                </Td>
                <Td>
                  <Badge colorScheme={getStatusColorScheme(result.status)}>{result.status}</Badge>
                </Td>
                <Td>
                  <Text fontSize='xs'>{result.bankReferenceId || result.partnerReferenceId || result.referenceId}</Text>
                </Td>
                <Td>
                  <Text fontSize='xs' color='red.600'>
                    {result.error}
                  </Text>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size='5xl'
      closeOnOverlayClick={!isSubmitting}
      closeOnEsc={!isSubmitting}
      scrollBehavior='inside'
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Bulk Partner Account Transfer</ModalHeader>
        <ModalCloseButton isDisabled={isSubmitting} />
        <ModalBody>
          <VStack spacing={4} align='stretch'>
            <FormControl isRequired isDisabled={isSubmitting}>
              <FormLabel>Partner</FormLabel>
              <Select
                placeholder='Select partner'
                value={selectedPartnerCode}
                onChange={(event) => setSelectedPartnerCode(event.target.value)}
              >
                {SUPPORTED_PARTNERS.map((partner) => (
                  <option key={partner.code} value={partner.code}>
                    {partner.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            {renderFormatHelp()}

            <FormControl isRequired isDisabled={isSubmitting}>
              <HStack mb={2} justify='space-between' align='center'>
                <FormLabel mb={0}>CSV file</FormLabel>
                <Button
                  size='xs'
                  variant='link'
                  colorScheme='blue'
                  onClick={() => downloadCsvFile(BULK_TRANSFER_SAMPLE_FILE_NAME, buildSampleCsvContent())}
                >
                  Download sample CSV
                </Button>
              </HStack>
              <Input type='file' accept='.csv,text/csv' onChange={handleFileChange} p={1} />
            </FormControl>

            {fileError !== '' && (
              <Alert status='error' borderRadius='md'>
                <AlertIcon />
                {fileError}
              </Alert>
            )}

            {invalidRows.length > 0 && (
              <Alert status='warning' borderRadius='md'>
                <AlertIcon />
                Fix the highlighted rows and upload the file again. Nothing is sent while any row is invalid.
              </Alert>
            )}

            {rows.length > 0 && results === null && renderPreview()}

            {isSubmitting && (
              <Box>
                <Text fontSize='sm' mb={1}>
                  Processing {processedCount} of {rows.length} transfers...
                </Text>
                <Progress
                  value={rows.length === 0 ? 0 : (processedCount / rows.length) * 100}
                  size='sm'
                  colorScheme='blue'
                  borderRadius='md'
                  hasStripe
                  isAnimated
                />
              </Box>
            )}

            {results !== null && renderResults()}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant='ghost' mr={3} onClick={handleClose} isDisabled={isSubmitting}>
            {results === null ? 'Cancel' : 'Close'}
          </Button>
          {results === null && (
            <Button colorScheme='blue' onClick={handleSubmit} isLoading={isSubmitting} isDisabled={isSubmitDisabled}>
              Initiate {rows.length > 0 ? rows.length : ''} Transfer(s)
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
